import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axios';

/**
 * Menu slice managing menu and dish state.
 */

// Fetch user's menus
export const fetchMenus = createAsyncThunk(
  'menus/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/v1/menus');
      return response.data.menus;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load menus');
    }
  }
);

// Fetch single menu
export const fetchMenu = createAsyncThunk(
  'menus/fetchOne',
  async (menuId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/v1/menus/${menuId}`);
      return response.data.menu;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load menu');
    }
  }
);

// Create menu
export const createMenu = createAsyncThunk(
  'menus/create',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/v1/menus', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.menu;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create menu');
    }
  }
);

// Update menu
export const updateMenu = createAsyncThunk(
  'menus/update',
  async ({ menuId, formData }, { rejectWithValue }) => {
    try {
      formData.append('_method', 'PUT');
      const response = await api.post(`/v1/menus/${menuId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.menu;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update menu');
    }
  }
);

// Delete menu
export const deleteMenu = createAsyncThunk(
  'menus/delete',
  async (menuId, { rejectWithValue }) => {
    try {
      await api.delete(`/v1/menus/${menuId}`);
      return menuId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete menu');
    }
  }
);

// Fetch dishes for a menu
export const fetchDishes = createAsyncThunk(
  'menus/fetchDishes',
  async (menuId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/v1/menus/${menuId}/dishes`);
      return response.data.dishes;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load dishes');
    }
  }
);

// Create dish
export const createDish = createAsyncThunk(
  'menus/createDish',
  async ({ menuId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/v1/menus/${menuId}/dishes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.dish;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create dish');
    }
  }
);

// Update dish
export const updateDish = createAsyncThunk(
  'menus/updateDish',
  async ({ menuId, dishId, formData }, { rejectWithValue }) => {
    try {
      formData.append('_method', 'PUT');
      const response = await api.post(
        `/v1/menus/${menuId}/dishes/${dishId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.dish;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update dish');
    }
  }
);

// Delete dish
export const deleteDish = createAsyncThunk(
  'menus/deleteDish',
  async ({ menuId, dishId }, { rejectWithValue }) => {
    try {
      await api.delete(`/v1/menus/${menuId}/dishes/${dishId}`);
      return dishId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete dish');
    }
  }
);

// Fetch categories
export const fetchCategories = createAsyncThunk(
  'menus/fetchCategories',
  async (menuId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/v1/menus/${menuId}/categories`);
      return response.data.categories;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load categories');
    }
  }
);

// Create category
export const createCategory = createAsyncThunk(
  'menus/createCategory',
  async ({ menuId, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/v1/menus/${menuId}/categories`, data);
      return response.data.category;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create category');
    }
  }
);

const initialState = {
  list: [],
  current: null,
  dishes: [],
  categories: [],
  loading: false,
  error: null,
};

const menuSlice = createSlice({
  name: 'menus',
  initialState,
  reducers: {
    clearCurrentMenu: (state) => {
      state.current = null;
      state.dishes = [];
      state.categories = [];
    },
    clearMenuError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all menus
      .addCase(fetchMenus.pending, (state) => { state.loading = true; })
      .addCase(fetchMenus.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single menu
      .addCase(fetchMenu.pending, (state) => { state.loading = true; })
      .addCase(fetchMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create menu
      .addCase(createMenu.pending, (state) => { state.loading = true; })
      .addCase(createMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload);
      })
      .addCase(createMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update menu
      .addCase(updateMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
        const idx = state.list.findIndex((m) => m.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
      })
      // Delete menu
      .addCase(deleteMenu.fulfilled, (state, action) => {
        state.list = state.list.filter((m) => m.id !== action.payload);
      })
      // Dishes
      .addCase(fetchDishes.fulfilled, (state, action) => {
        state.dishes = action.payload;
      })
      .addCase(createDish.fulfilled, (state, action) => {
        state.dishes.push(action.payload);
      })
      .addCase(updateDish.fulfilled, (state, action) => {
        const idx = state.dishes.findIndex((d) => d.id === action.payload.id);
        if (idx !== -1) state.dishes[idx] = action.payload;
      })
      .addCase(deleteDish.fulfilled, (state, action) => {
        state.dishes = state.dishes.filter((d) => d.id !== action.payload);
      })
      // Categories
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload);
      });
  },
});

export const { clearCurrentMenu, clearMenuError } = menuSlice.actions;
export default menuSlice.reducer;
